import { useBillingActionsPermission } from "@/shared/hooks/useBillingActions";
import { useBillingProfileForm } from "@/shared/hooks/useBillingProfileForm";
import type { BillingProfileStepState } from "@/shared/hooks/useBillingProfileStep";
import { useBillingSummaryQuery } from "@/shared/hooks/useBillingSummary";
import { Alert } from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Form } from "@/shared/ui/form";
import { BillingProfileFields } from "./BillingProfileFields";

interface BillingProfileStepProps {
  step: BillingProfileStepState;
  /** The flow's own mutation is in flight (a retry started with `mutate` settles after `proceed` returns). */
  busy?: boolean;
  /** "Back" inside a dialog that has a previous step; "Cancel" where the step IS the dialog. */
  backLabel?: string;
}

/**
 * The billing details as a step of whichever dialog was refused for want of them (Kumar, 2026-09-29). It
 * renders the whole content of that dialog — its own title included — so the dialog swaps its body for
 * this one and swaps back on Back with every choice it held still in place.
 *
 * The profile it opens on is `GET /billing`'s, so a partial one (a billing email the gateway turned out
 * to need) is corrected rather than typed again. There is no unsaved-changes guard here, unlike the card:
 * the step is modal, and its only ways out are Back and the dialog's own close.
 */
export function BillingProfileStep({ step, busy = false, backLabel = "Back" }: BillingProfileStepProps) {
  const canManage = useBillingActionsPermission();
  const billing = useBillingSummaryQuery(canManage);
  const { form, save, submit } = useBillingProfileForm(billing.data?.profile ?? null, step.refusal);
  const working = save.isPending || step.continuing || busy;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Billing details</DialogTitle>
        <DialogDescription>
          Your invoice needs these details. We&apos;ll continue to payment once they&apos;re saved.
        </DialogDescription>
      </DialogHeader>
      {canManage ? (
        <Form {...form}>
          <form onSubmit={submit(step.proceed)} noValidate className="flex flex-col gap-4">
            <BillingProfileFields control={form.control} canManage />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={step.back} disabled={working}>
                {backLabel}
              </Button>
              <Button type="submit" variant="primary" loading={working}>
                Save and continue
              </Button>
            </DialogFooter>
          </form>
        </Form>
      ) : (
        <>
          <Alert tone="info" title="Read only">
            Only an owner can change the billing details.
          </Alert>
          <DialogFooter>
            <Button variant="ghost" onClick={step.back}>
              {backLabel}
            </Button>
          </DialogFooter>
        </>
      )}
    </>
  );
}
